import { useContext, useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import Sidebar from '../Sidebar';
import '../../../Pages/admin/style.scss';
import { AppContext } from '../../../context';
import { Alert, Button } from 'reactstrap';
import {
  FETCH_NEW_NOTIFICATIONS,
  PENDING_ACTIONS_CATEGORIES,
  PENDING_ACTIONS_SCHEDULER,
} from '../../../Pages/notifications/gql';
import { useLazyQuery, useMutation } from '@apollo/client';
import NotificationBar from '../../../Pages/notifications/components/NotificationBar';
import { toast, ToastContainer } from 'react-toastify';
import Notifications from '../../../components/layout/Notification';
import io, { Socket } from 'socket.io-client';
import { DefaultEventsMap } from '@socket.io/component-emitter';
import PlacementCelebrationContainer, {
  usePlacementCelebrations,
} from '../../PlacementCelebration/PlacementCelebrationContainer';
import { clearConfetti } from '../../PlacementCelebration/confetti';
import { PlacementCelebrationPayload } from '../../PlacementCelebration/PlacementCelebrationToast';
import {
  UNSEEN_PLACEMENT_CELEBRATIONS,
  MARK_PLACEMENT_CELEBRATIONS_SEEN,
} from '../../PlacementCelebration/gql';
import { getImpersonatorId } from '../../../utils/helper';
interface mainLayoutPropType {
  history?: RouteComponentProps;
  children: JSX.Element;
}

/**
 * Revokes a refresh token server-side via a bare fetch (bypasses the Apollo
 * link, mirroring performTokenRefresh). Best-effort: exiting impersonation must
 * never be blocked by a failed revoke. The `logout` mutation is unguarded.
 */
const revokeRefreshToken = async (token: string): Promise<void> => {
  try {
    await fetch(
      process.env.REACT_APP_GRAPHQL_URI || 'http://localhost:3001/graphql',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `mutation Logout($token: String) { logout(token: $token) { status message } }`,
          variables: { token },
        }),
      }
    );
  } catch {
    // swallow — impersonation exit should proceed regardless
  }
};

export const loginAsWithPrevious = async () => {
  const preToken = window.localStorage.previousToken;
  const previousSideBarItem = window.localStorage.previousSideBarItem;
  const previousRefreshToken = window.localStorage.previousRefreshToken;
  const currentToken = window.localStorage.token;
  if (preToken && currentToken) {
    // Revoke the impersonated session's refresh token before restoring the
    // admin's own, so it can't be silently refreshed for 7 days afterward.
    const impersonationRefreshToken = window.localStorage.refreshToken;
    if (impersonationRefreshToken) {
      await revokeRefreshToken(impersonationRefreshToken);
    }
    window.localStorage.removeItem('token');
    window.localStorage.removeItem('sideBarItem');
    window.localStorage.removeItem('refreshToken');
    window.localStorage.setItem('token', preToken);
    window.localStorage.setItem('sideBarItem', previousSideBarItem);
    previousRefreshToken
      ? window.localStorage.setItem('refreshToken', previousRefreshToken)
      : window.localStorage.removeItem('refreshToken');
    window.localStorage.removeItem('previousToken');
    window.localStorage.removeItem('previousRefreshToken');
    window.localStorage.removeItem('previousSideBarItem');
    window.location.reload();
  }
};

const MainLayout = ({ children, history }: mainLayoutPropType): JSX.Element => {

  const [isMenuActive, setIsMenuActive] = useState<boolean>(false);
  const [isOpen, setOpen] = useState<boolean>(false);
  const socket = useRef<Socket<DefaultEventsMap, DefaultEventsMap> | undefined>();
  const [pendingActionFromSockets, setPendingActionFromSockets] = useState(null);

  const { toasts, enqueueCelebration, dismissCelebration } = usePlacementCelebrations();
  // One-shot latch: fire the unseen-celebrations query exactly once per session
  const hasReplayedCelebrations = useRef(false);

  // Safety net: once every celebration toast is gone, clear the confetti canvas
  // so a dropped animation frame can't leave a lingering streak on screen.
  useEffect(() => {
    if (toasts.length === 0) {
      clearConfetti();
    }
  }, [toasts.length]);

  const { user } = useContext(AppContext);
  const page = new URLSearchParams(history?.location.search).get('page') || '1';
  const _currentPage: number = parseInt(page);
  const [currentPage] = useState(_currentPage);
  const [limit] = useState(5);
  const [searchParams] = useState<string>('');
  const [statusFilter] = useState<string>('');

  const [
    pedingActionOfAllCategories,
    { data: pedingActions, loading: loaderForPendingAction },
  ] = useLazyQuery(PENDING_ACTIONS_CATEGORIES);

  const [PendingActionScheduler, { loading: loaderForRecount }] = useMutation(
    PENDING_ACTIONS_SCHEDULER
  );

  const [fetchAllNewNotifications] = useLazyQuery(FETCH_NEW_NOTIFICATIONS, {
    onCompleted: (data) => {
      data?.allNewNotifications.notifications?.forEach(
        (n: {
          alertMsg: string;
          createdAt: Date | string;
          id: string;
          notifyableType: string;
          notifyableId?: string | null;
          alertType: string;
        }) =>
          toast(
            <NotificationBar
              alertMsg={n.alertMsg}
              date={n.createdAt}
              id={n.id}
              notifyableType={n.notifyableType}
              // kan-111: the AI reference alert routes to /view-candidate/<notifyableId>;
              // without the id the bar fell back to the Settings tab.
              notifyableId={n.notifyableId}
              alertType={n.alertType}
            />,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              toastId: n.id,
              autoClose: 3000,
              draggable: false,
              closeButton: false,
            }
          )
      );
    },
  });

  const [markPlacementCelebrationsSeen] = useMutation(
    MARK_PLACEMENT_CELEBRATIONS_SEEN,
  );

  const [fetchUnseenCelebrations, { data: unseenCelebrationData }] = useLazyQuery(
    UNSEEN_PLACEMENT_CELEBRATIONS,
    { fetchPolicy: 'network-only' },
  );

  // Process results via a data-effect rather than `onCompleted`. Apollo Client
  // 3.7's useLazyQuery `onCompleted` fires unreliably (notably for a single
  // one-shot invocation), which silently drops the toast. Watching `data` is
  // the robust, recommended pattern.
  useEffect(() => {
    const celebrations: PlacementCelebrationPayload[] =
      unseenCelebrationData?.unseenPlacementCelebrations ?? [];
    if (celebrations.length === 0) return;

    const idsToMark: string[] = [];
    celebrations.forEach((c: PlacementCelebrationPayload) => {
      enqueueCelebration(c);
      idsToMark.push(c.id);
    });

    markPlacementCelebrationsSeen({
      variables: { celebrationIds: idsToMark },
    }).catch(() => {
      /* best-effort: worst case a re-replay on next login */
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unseenCelebrationData]);

  const fetchNewtNotifications = () => {
    fetchAllNewNotifications({
      variables: {
        limit,
        page: currentPage,
        searchText: searchParams,
        alertStatus: statusFilter,
        popUpDismissal: false,
      },
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPendingActions = () => {
    pedingActionOfAllCategories();
  };

  useEffect(() => {
    fetchPendingActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location?.pathname]);

  useEffect(() => {
    if (!!socket) {
      if (
        !socket.current || !socket.current.connected) {
        socket.current = io(process.env.REACT_APP_WEBSOCKET_BASEURL || "localhost:3001", {
          path: '/websockets',
          transports: ['websocket'],
        });
      }
      socket?.current?.emit('connection', { userId: user?.id });
      socket?.current?.on('heart-beat', (_) => { });
      socket?.current?.on('pendingActionUpdater', (data: any) => {
        setPendingActionFromSockets(data)
      });
      // `.off()` first so this effect re-running (deps change / re-render) can't
      // stack duplicate listeners on a still-connected socket.
      socket?.current?.off('placement-celebration');
      socket?.current?.on('placement-celebration', () => {
        // The socket payload carries only an opaque id (no PII). Pull the real
        // celebration details over the authenticated GraphQL query; results are
        // handled by the `unseenCelebrationData` effect below (enqueue + mark-seen).
        fetchUnseenCelebrations();
      });

      // One-shot login-replay: run exactly once per authenticated session
      if (!hasReplayedCelebrations.current && user?.id) {
        hasReplayedCelebrations.current = true;
        fetchUnseenCelebrations();
      }

      return () => {
        socket?.current?.close();
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedingActions, user?.id]);

  useEffect(() => {
    fetchNewtNotifications();

    // eslint-disable-next-line
  }, [window.location?.pathname]);

  const toggleNotification = () => setOpen(!isOpen);

  useEffect(() => {
    window.scrollTo(100, 0)
  }, []);

  // Banner visibility is driven by decoding the JWT `impersonatorId` marker
  // claim (server-authoritative), NOT the localStorage `previousToken` stash
  // flag — see contract §4/§5. Re-checked on every render since MainLayout
  // re-renders on route change (survives all private routes via PrivateRoute).
  const isImpersonating = getImpersonatorId() !== null;
  const impersonationBannerHeight = 44;

  return (
    <div className="wrapper">
      {isImpersonating && (
        <Alert
          color="primary"
          className="p-2 px-3 d-flex justify-content-center align-items-center text-dark m-0 rounded-0"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1050,
            height: impersonationBannerHeight,
          }}
        >
          You are viewing account as &nbsp;
          <b className="font-weight-bold">{user?.fullName}</b>,
          <Button
            className="border-0 bg-transparent text-left text-primary py-0 px-1"
            onClick={() => loginAsWithPrevious()}
          >
            <u>click here</u>
          </Button>
          to revert back to your account.
        </Alert>
      )}
      <Notifications
        isOpen={isOpen}
        toggle={toggleNotification}
        pedingActions={pendingActionFromSockets ? pendingActionFromSockets : pedingActions}
        PendingActionScheduler={PendingActionScheduler}
        loaderForRecount={loaderForRecount}
        pedingActionOfAllCategories={pedingActionOfAllCategories}
        loaderForPendingAction={loaderForPendingAction}
        userId={user?.id}
        pendingActionFromSockets={pendingActionFromSockets}
      />

      <ToastContainer autoClose={3000} />
      <Sidebar
        isOpen={isMenuActive}
        isMenuActive={isMenuActive}
        setIsMenuActive={setIsMenuActive}
      />

      <div
        className={isMenuActive ? 'hamburger is-active' : 'hamburger'}
        onClick={() => {
          setIsMenuActive(!isMenuActive);
        }}
      >
        <span className="line"></span>
        <span className="line"></span>
        <span className="line"></span>
      </div>
      <div
        className="main-container"
        style={isImpersonating ? { paddingTop: impersonationBannerHeight } : undefined}
        onClick={() => {
          if (isMenuActive) {
            setIsMenuActive(!isMenuActive);
          }
        }}
      >
        {children}
      </div>

      <PlacementCelebrationContainer
        toasts={toasts}
        onDismiss={dismissCelebration}
      />
    </div>
  );
};

export default MainLayout;
